import React, { useEffect, useState } from "react";
import { useAppDispatch } from "@redux/hooks";
import { tagApi } from "@api/tags";
import "./Tags.css";
import { useLocation } from "react-router-dom";
import { useUser } from "@hooks/useUser";
import { usePreload } from "@hooks/usePreload";
import { fetchPreload } from "@redux/slices/preloadSlice";
import { TagType } from "types";
import LlaHeader from "./LlaHeader";

type TagsTextType = {
  [key: number]: {
    english?: string;
    japanese?: string;
  };
};

const Tags: React.FC = () => {
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const adto = queryParams.get("adto");
  const lla = queryParams.get("lla"); // LiveLet admin page = 1

  const user = useUser();
  const { userKey, userToken } = user;
  const preload = usePreload();
  const existingTags = preload.tags;
  const dispatch = useAppDispatch();

  const [tagsText, setTagsText] = useState<TagsTextType>({});
  const [isAddingParent, setIsAddingParent] = useState(false);
  const [isAddingChild, setIsAddingChild] = useState<number | null>(null);
  const [newTag, setNewTag] = useState({ english: "", japanese: "", parentKey: null });

  useEffect(() => {
    if (existingTags) {
      const initialTags: TagsTextType = {};
      existingTags.forEach((tag: TagType) => {
        initialTags[tag.tagKey] = {
          english: tag.english || "",
          japanese: tag.japanese || "",
        };
      });
      setTagsText(initialTags);
    }
  }, [existingTags]);

  const handleUpdate = async (tagKey: number) => {
    if (!userKey || !userToken) return;
    try {
      const languageValues = { ...tagsText[tagKey] };
      await tagApi.editTag(userKey, userToken, tagKey, languageValues);
      dispatch(fetchPreload());
    } catch (error) {
      console.error("Failed to update tag:", error);
    }
  };

  const handleDelete = async (tagKey: number) => {
    if (!userKey || !userToken) return;
    const hasChildren = existingTags && existingTags.some((tag: TagType) => tag.parentKey === tagKey);
    if (hasChildren) {
      alert("Cannot delete parent tag with children.");
      return;
    }
    try {
      await tagApi.deleteTag(tagKey, userKey, userToken);
      dispatch(fetchPreload());
    } catch (error) {
      console.error("Failed to delete tag:", error);
    }
  };

  const handleCreate = async () => {
    if (!userKey || !userToken) return;
    try {
      const parentKey = isAddingChild || null;
      const languageValues = { english: newTag.english, japanese: newTag.japanese };
      await tagApi.saveTag(userKey, userToken, { ...languageValues }, parentKey);
      setIsAddingParent(false);
      setIsAddingChild(null);
      setNewTag({ english: "", japanese: "", parentKey: null });
      dispatch(fetchPreload());
    } catch (error) {
      console.error("Failed to create tag:", error);
    }
  };

  const parentTags = existingTags && existingTags.filter((tag: TagType) => !tag.parentKey) || [];
  const getChildren = (parentKey: number) => existingTags && existingTags.filter((tag: TagType) => tag.parentKey === parentKey) || [];
  const nativeTitle = <span style={{fontSize: "1.25rem", fontWeight: "bold", marginBottom: "0.5rem"}}>タグの管理</span>

  return (
    <div className="tag-container" style={{backgroundColor: "#eef5f9"}}>
      {(lla === "1" && false) ? <LlaHeader headerText="タグの管理" /> : nativeTitle}
      <div className='bg-white drop-shadow-md p-5 rounded-md'>
        {parentTags.map((parent) => (
          <div key={parent.tagKey} className="p-3 border-b">
            <div className="flex gap-2 items-center">
              <input
                type="text"
                value={tagsText[parent.tagKey]?.japanese || ""}
                onChange={(e) => setTagsText({ ...tagsText, [parent.tagKey]: { ...tagsText[parent.tagKey], japanese: e.target.value } })}
                className="datum-input"
              />
              <input
                type="text"
                value={tagsText[parent.tagKey]?.english || ""}
                onChange={(e) => setTagsText({ ...tagsText, [parent.tagKey]: { ...tagsText[parent.tagKey], english: e.target.value } })}
                className="datum-input"
                style={{display: "none"}}
              />
              <button onClick={() => handleUpdate(parent.tagKey)} className="add-button" style={{width: "10rem"}}>更新</button>
              <button onClick={() => handleDelete(parent.tagKey)} className="add-button" style={{width: "10rem", backgroundColor: "#a22"}}>削除</button>
            </div>
            <div>
              {getChildren(parent.tagKey).map((child) => (
                <div key={child.tagKey} className="flex gap-2 mt-2 items-center">
                  <input
                    type="text"
                    value={tagsText[child.tagKey]?.japanese || ""}
                    onChange={(e) => setTagsText({ ...tagsText, [child.tagKey]: { ...tagsText[child.tagKey], japanese: e.target.value } })}
                    className="datum-input"
                    style={{marginLeft: "1.5rem"}}
                  />
                  <input
                    type="text"
                    value={tagsText[child.tagKey]?.english || ""}
                    onChange={(e) => setTagsText({ ...tagsText, [child.tagKey]: { ...tagsText[child.tagKey], english: e.target.value } })}
                    className="datum-input"
                    style={{marginLeft: "1.5rem", display: "none"}}
                  />
                  <button onClick={() => handleUpdate(child.tagKey)} className="add-button" style={{width: "10rem"}}>更新</button>
                  <button onClick={() => handleDelete(child.tagKey)} className="add-button" style={{width: "10rem", backgroundColor: "#a22"}}>削除</button>
                </div>
              ))}
              {isAddingChild === parent.tagKey ? (
                <div className="flex gap-2">
                  <input type="text" placeholder="Japanese" value={newTag.japanese} onChange={(e) => setNewTag({ ...newTag, japanese: e.target.value })} className="datum-input" />
                  <input type="text" placeholder="English" value={newTag.english} onChange={(e) => setNewTag({ ...newTag, english: e.target.value })} className="datum-input" style={{display: "none"}} />
                  <button onClick={handleCreate} className="add-button">保存</button>
                  <button onClick={() => setIsAddingChild(null)} className="add-button">閉じる</button>
                </div>
              ) : (
                <button onClick={() => {setIsAddingParent(false); setIsAddingChild(parent.tagKey)}} className="btn btn-primary mt-2 ml-6">+ オプションを追加</button>
              )}
            </div>
          </div>
        ))}
        {isAddingParent && (
          <div className="flex gap-2">
            <input type="text" placeholder="Japanese" value={newTag.japanese} onChange={(e) => setNewTag({ ...newTag, japanese: e.target.value })} className="datum-input" />
            <input type="text" placeholder="English" value={newTag.english} onChange={(e) => setNewTag({ ...newTag, english: e.target.value })} className="datum-input" style={{display: "none"}} />
            <button onClick={handleCreate} className="add-button">保存</button>
            <button onClick={() => setIsAddingParent(false)} className="add-button">閉じる</button>
          </div>
        )}
      </div>
      <button onClick={() => {setIsAddingChild(null); setIsAddingParent(true)}} className="btn btn-primary mt-4">+ タグを追加</button>
    </div>
  );
};

export default Tags;
